import JSDB from 'fromKotlin/nk';

const API_BASE = 'https://nihulkav.online';

/*
Paste into apiConfig.credit (object or JSON string).

Tranzila (provider omitted):
{
  "terminal_name": "YOUR_TOKEN_API_TERMINAL",
  "iframe_terminal_name": "YOUR_HOSTED_IFRAME_TERMINAL",
  "app_key": "YOUR_APP_KEY",
  "secret": "YOUR_SECRET"
}

PayPlus:
{
  "provider": "payplus",
  "api_key": "API_KEY",
  "secret_key": "SECRET_KEY",
  "payment_page_uid": "PAYMENT_PAGE_UID",
  "terminal_uid": "TERMINAL_UID",
  "cashier_uid": "CASHIER_UID",
  "sandbox": false
}
*/

export interface TranzilaConfig {
    present: true;
    provider: 'tranzila' | 'payplus';
}

/**
 * Existence check only. Credentials live server-side — we never send secrets from the browser.
 * Returned object is an opaque token used by the UI to enable the charge flow.
 */
export function getTranzilaConfig(): TranzilaConfig | null {
    try {
        const raw = (JSDB().apiConfig as any)?.credit;
        if (!raw) return null;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const provider = String(parsed?.provider || 'tranzila').trim().toLowerCase();
        if (provider === 'payplus') {
            if (!parsed?.api_key || !parsed?.secret_key || !parsed?.payment_page_uid) return null;
            return {present: true, provider: 'payplus'};
        }
        if (!parsed?.terminal_name || !parsed?.app_key || !parsed?.secret) return null;
        return {present: true, provider: 'tranzila'};
    } catch {
        return null;
    }
}

function getHeaders(): HeadersInit {
    const token = (JSDB() as any).getToken?.();
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
}

function getCompanyId(): string {
    try {
        const db = JSDB() as any;
        if (typeof db.getCompanyId === 'function') return String(db.getCompanyId());
        return '';
    } catch {
        return '';
    }
}

export interface TranzilaChargeResult {
    success: boolean;
    transaction_id?: string;
    token?: string;
    token_expiry?: string;
    error?: string;
    raw?: any;
}

export interface TranzilaClientInfo {
    external_id?: string;
    name?: string;
    contact_person?: string;
    id?: string;
    email?: string;
    phone_country_code?: string;
    phone_area_code?: string;
    phone_number?: string;
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    country_code?: string;
    zip?: string;
}

export interface ChargeWithTokenParams {
    config: TranzilaConfig;
    token: string;
    expiry: string;
    amount: number;
    /** מספר תשלומים (1 = חיוב בודד). מועבר ל-Tranzila כ־payment_plan */
    payment_plan?: number;
    clientName?: string;
    clientEmail?: string;
    card_holder_id?: string;
    client?: TranzilaClientInfo;
}

export interface ChargeWithCardParams {
    config: TranzilaConfig;
    card_number: string;
    expire_month: number;
    expire_year: number;
    cvv: string;
    card_holder_id: string;
    amount: number;
    /** מספר תשלומים (1 = חיוב בודד). מועבר ל-Tranzila כ־payment_plan */
    payment_plan?: number;
    clientName?: string;
    clientEmail?: string;
    client?: TranzilaClientInfo;
}

function unwrapStatusMsg(json: any): any {
    if (!json) return null;
    const m = json.status_msg ?? json.data ?? json.msg;
    if (m == null) return null;
    if (typeof m === 'object') return m;
    if (typeof m !== 'string') return null;
    const trimmed = m.trim();
    try {
        return JSON.parse(trimmed);
    } catch {}
    try {
        return JSON.parse(
            trimmed
                .replace(/'/g, '"')
                .replace(/\bTrue\b/g, 'true')
                .replace(/\bFalse\b/g, 'false')
                .replace(/\bNone\b/g, 'null'),
        );
    } catch {}
    return null;
}

async function postCharge(body: Record<string, any>): Promise<TranzilaChargeResult> {
    try {
        const res = await fetch(`${API_BASE}/tranzila/charge`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({company_id: getCompanyId(), ...body}),
        });
        const json = await res.json();
        if (json?.status === 'SUCCESS') {
            const payload = unwrapStatusMsg(json);
            const p = Array.isArray(payload) ? payload[0] : payload;
            return {
                success: true,
                transaction_id: p?.transaction_id,
                token: p?.token
                    || p?.token_value
                    || p?.card_token
                    || p?.raw?.token
                    || p?.raw?.transaction_result?.token
                    || p?.raw?.transaction_result?.token_value,
                token_expiry: p?.token_expiry,
                raw: p?.raw ?? p,
            };
        }
        return {
            success: false,
            error: json?.status_msg || json?.msg || 'סליקה נכשלה',
            raw: json,
        };
    } catch (e: any) {
        return {success: false, error: e?.message || 'שגיאת רשת'};
    }
}

function cleanClient(c?: TranzilaClientInfo): TranzilaClientInfo | undefined {
    if (!c) return undefined;
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(c)) {
        if (v === undefined || v === null) continue;
        if (typeof v === 'string' && !v.trim()) continue;
        out[k] = v;
    }
    return Object.keys(out).length ? (out as TranzilaClientInfo) : undefined;
}

export async function chargeWithToken(params: ChargeWithTokenParams): Promise<TranzilaChargeResult> {
    const {token, expiry, amount, payment_plan, clientName, clientEmail, card_holder_id, client} = params;
    return postCharge({
        amount,
        token,
        expiry,
        card_holder_id,
        client_name: clientName,
        client_email: clientEmail,
        client: cleanClient(client),
        ...(payment_plan != null && payment_plan > 0 ? {payment_plan} : {}),
    });
}

export async function chargeWithCard(params: ChargeWithCardParams): Promise<TranzilaChargeResult> {
    const {card_number, expire_month, expire_year, cvv, card_holder_id, amount, payment_plan, clientName, clientEmail, client} = params;
    return postCharge({
        amount,
        card_number,
        expire_month,
        expire_year,
        cvv,
        card_holder_id,
        client_name: clientName,
        client_email: clientEmail,
        client: cleanClient(client),
        ...(payment_plan != null && payment_plan > 0 ? {payment_plan} : {}),
    });
}
